import classnames from 'classnames';
import Label from 'components/inputs/Label';
import {
  fileExtensionRegex,
  imageFileExtensions,
  imageMimeTypes,
} from 'constants/files';
import { List } from 'immutable';
import React from 'react';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { RFIAttachment, RFIAttachmentTypes } from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { RFIForwardActivity } from 'records/RFIForwardActivity';
import { SheetVersion } from 'records/SheetVersion';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { useTranslator, useDateFormatter } from '@bluebeam/react-i18n';
import {
  AttachmentTypeEnum,
  RemoveAttachmentTypes,
} from '../ViewRFIOverlay/ViewRFIMachine/viewRfiMachineTypes';
import RFIFilesListItem from './RFIFilesListItem';

interface Props {
  sheetVersions: List<SheetVersion>;
  files: List<RFIFile>;
  attachments: List<RFIAttachment>;
  projectDriveItems?: List<ProjectDriveSelectedItem>;
  integrationItems?: List<IntegrationsSelectedItem>;
  studioItems: List<StudioProjectFileData>;
  onAttachmentRemove: (
    attachment: RemoveAttachmentTypes,
    source: AttachmentTypeEnum
  ) => void;
  discussionAttachments?: List<RFIForwardActivity>;
  onDiscussionAttachmentRemove?: (id: string) => void;
}

const RFIFilesList: React.FC<Props> = (props) => {
  const {
    files,
    attachments,
    sheetVersions,
    projectDriveItems,
    integrationItems,
    studioItems,
    discussionAttachments,
    onAttachmentRemove,
    onDiscussionAttachmentRemove,
  } = props;

  const getRFIAttachmentTypeFromFileName = (fileName: string) => {
    const fileExtensionMatch = fileName.match(fileExtensionRegex);

    if (fileExtensionMatch && imageFileExtensions.has(fileExtensionMatch[0])) {
      return RFIAttachmentTypes.IMAGE;
    }
    return RFIAttachmentTypes.FILE;
  };

  const attachmentCount =
    files.size +
    sheetVersions.size +
    attachments.size +
    (projectDriveItems ? projectDriveItems.size : 0) +
    (integrationItems ? integrationItems.size : 0) +
    (studioItems ? studioItems.size : 0) +
    (discussionAttachments ? discussionAttachments.size : 0);

  const { translate } = useTranslator(['rfiPage']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
  });

  return (
    <div>
      <Label
        label={`${translate('rfiPage:attachments')} (${attachmentCount})`}
      />
      <div
        className={classnames('oa color-gray-6', {
          'bt-2 bl-2 br-2 b-radius-3 mb3': attachmentCount > 0,
          mt2: attachmentCount === 0,
        })}
      >
        {attachments.map((a) => (
          <RFIFilesListItem
            key={a.fileid}
            text={a.name}
            type={a.type}
            onRemove={() => onAttachmentRemove(a, AttachmentTypeEnum.SAVED)}
          />
        ))}
        {discussionAttachments &&
          discussionAttachments.map((a) => (
            <RFIFilesListItem
              key={a.id}
              text={translate('rfiPage:rfiDetails.attachmentsFrom', {
                name: `${a.user.profile.firstname} ${a.user.profile.lastname}`,
                date: dateFormatter?.format(new Date(a.timestamp * 1000)),
              })}
              type={RFIAttachmentTypes.DISCUSSION}
              onRemove={() =>
                onDiscussionAttachmentRemove &&
                onDiscussionAttachmentRemove(a.id)
              }
            />
          ))}
        {Array.from(files).map((rfiFile) => (
          <RFIFilesListItem
            key={rfiFile.file.name}
            text={rfiFile.file.name}
            type={
              imageMimeTypes.has(rfiFile.file.type)
                ? RFIAttachmentTypes.IMAGE
                : RFIAttachmentTypes.FILE
            }
            onRemove={() =>
              onAttachmentRemove(rfiFile.file, AttachmentTypeEnum.FILE)
            }
          />
        ))}
        {sheetVersions.map((sv) => (
          <RFIFilesListItem
            key={sv.pageid}
            text={sv.sheetnumber}
            type={RFIAttachmentTypes.DRAWING}
            onRemove={() =>
              onAttachmentRemove(sv.pageid, AttachmentTypeEnum.DRAWING)
            }
          />
        ))}
        {(projectDriveItems || List())
          .filter(({ itemType }) => itemType === 'file')
          .map((item) => {
            return (
              <RFIFilesListItem
                key={item.id}
                text={item.name}
                type={getRFIAttachmentTypeFromFileName(item.itemType)}
                onRemove={() =>
                  onAttachmentRemove(item, AttachmentTypeEnum.DRIVE)
                }
              />
            );
          })}
        {(integrationItems || List())
          .filter(({ itemType }) => itemType === 'file')
          .map((integration) => {
            return (
              <RFIFilesListItem
                key={integration.item.id}
                text={integration.item.name}
                type={getRFIAttachmentTypeFromFileName(integration.itemType)}
                onRemove={() =>
                  onAttachmentRemove(
                    integration,
                    AttachmentTypeEnum.INTEGRATION
                  )
                }
              />
            );
          })}
        {(studioItems || List()).map((item) => {
          return (
            <RFIFilesListItem
              key={item.id}
              text={item.name}
              type={getRFIAttachmentTypeFromFileName('file')}
              onRemove={() =>
                onAttachmentRemove(item, AttachmentTypeEnum.STUDIO)
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default RFIFilesList;
